import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { getOnboardingStatus } from '@/lib/onboarding';
import type { OnboardingStatus } from '@/lib/onboarding';

// OnboardingStatus interface now imported from lib/onboarding

export function useOnboardingStatus() {
  const { user } = useAuth();
  const [status, setStatus] = useState<OnboardingStatus>({
    hasProfile: false,
    hasUsername: false,
    hasAvatar: false,
    importComplete: false,
    bookshelfScanComplete: false,
    genresComplete: false,
    archetypeComplete: false,
    readingGoalSet: false,
    genreCount: 0,
    nextStep: 'profile',
    loading: true
  });

  const checkOnboardingStatus = useCallback(async () => {
    if (!user) {
      setStatus({ 
        hasProfile: false,
        hasUsername: false,
        hasAvatar: false,
        importComplete: false,
        bookshelfScanComplete: false,
        genresComplete: false,
        archetypeComplete: false,
        readingGoalSet: false,
        genreCount: 0,
        nextStep: 'profile',
        loading: false 
      });
      return;
    }

    try {
      setStatus(prev => ({ ...prev, loading: true, error: undefined }));
      
      // Add timeout protection to prevent infinite loading
      const timeoutPromise = new Promise<OnboardingStatus>((_, reject) => {
        setTimeout(() => reject(new Error('Onboarding status check timeout after 3s')), 3000);
      });
      
      const statusPromise = getOnboardingStatus(user.id);
      
      const onboardingStatus = await Promise.race([statusPromise, timeoutPromise]);
      setStatus(onboardingStatus);
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      // On timeout or error, assume onboarding is complete to avoid blocking users
      setStatus({ 
        hasProfile: true,
        hasUsername: true,
        hasAvatar: false,
        importComplete: true,
        bookshelfScanComplete: false,
        genresComplete: true,
        archetypeComplete: true,
        readingGoalSet: false,
        genreCount: 3,
        nextStep: 'done',
        loading: false, 
        error: 'Failed to check onboarding status - proceeding to app' 
      });
    }
  }, [user]);

  // Auto-check status on mount and when user changes
  useEffect(() => {
    checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  // If onboarding status check itself is stuck loading for too long, fail open
  useEffect(() => {
    if (!status.loading) return;

    const timeout = setTimeout(() => {
      console.warn('OnboardingStatus: global timeout reached, marking as complete to avoid blocking');
      setStatus(prev => ({
        ...prev,
        loading: false,
        nextStep: 'done',
      }));
    }, 5000);

    return () => clearTimeout(timeout);
  }, [status.loading]);

  return { ...status, refresh: checkOnboardingStatus };
}